import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSmartBack } from "../../utils/useSmartBack";
import {
  Alert,
  AutoComplete,
  Badge,
  Button,
  Card,
  Dropdown,
  Form,
  Input,
  Modal,
  Select,
  Spin,
  Tabs,
  Tooltip,
  message,
} from "antd";
import {
  CloudSyncOutlined,
  DownOutlined,
  LeftOutlined,
  LinkOutlined,
  SearchOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import PageLogoutButton from "../PageLogoutButton";
import NavigationDropdown from "../NavigationDropDown";
import EmailThreadPanel from "./EmailThreadPanel";
import EmailFolderSidebar from "./EmailFolderSidebar";
import { PendingLinkList, UnmatchedEmailList } from "./EmailInboxLists";
import {
  connectMailbox,
  disconnectMailbox,
  getMailboxStatus,
  getLinkedKeys,
  getUnmatchedEmails,
  getEmailThread,
  triggerEmailSync,
  DEALER_EMAIL_MODELS_STORAGE_KEY,
  type EmailLinkType,
  type MailboxSyncStatus,
  type MailboxCredentials,
  type LinkedKey,
  type RecordKey,
} from "../../utils/emailLinkAPI";
import { PRIMARY_BUTTON_CLASS, formatDateTime } from "./emailLinkShared";

// The whole page shell for BOTH Email tabs (customer and dealer), switched
// from the folder sidebar on the left rather than two separate routes.
//
// Part 1 (customer) and Part 2 (buy side) are the same page with a different
// linkType and a different label — so switching sides is a config swap, not
// a second implementation. Anything genuinely side-specific belongs in
// SIDE_CONFIG, never in a branch further down this file.

export type EmailSide = "customer" | "dealer";

interface EmailSideConfig {
  linkType: EmailLinkType;
  /** Page heading, e.g. "Customer Email". */
  title: string;
  /** One-line explanation under the heading. */
  subtitle: string;
  /** What the match key is called, e.g. "Quote" or "Model". */
  keyLabel: string;
  /** Placeholder for the key picker. */
  keyPlaceholder: string;
  /** URL query param that pre-selects a record, e.g. "quoteId" or "model". */
  keyParam: string;
  /**
   * Lets the key picker select several records at once (buy-side model
   * picker). The selection is stored and put on the URL as a comma-separated
   * list, e.g. ?model=A,B,C. Customer Email leaves this off and keeps the
   * single-value picker unchanged.
   */
  multiSelectKey?: boolean;
}

const SIDE_CONFIG: Record<EmailSide, EmailSideConfig> = {
  customer: {
    linkType: "quote",
    title: "Customer Email",
    subtitle:
      "Every customer email linked to its quote — full history, attachments, and reply from your email.",
    keyLabel: "Quote",
    keyPlaceholder: "Enter or pick a quote number",
    keyParam: "quoteId",
  },
  dealer: {
    linkType: "dealer",
    title: "Buy-Side (Dealer) Email",
    subtitle:
      "Every dealer email linked to what they're offering — offers, negotiation history, attachments, and reply from your email.",
    keyLabel: "Model",
    keyPlaceholder: "Enter or pick model numbers",
    keyParam: "model",
    multiSelectKey: true,
  },
};

// Same square, outset-border toolbar button used for "Back" on the Quotes
// page's CommonHeader — kept identical here so the two pages match.
const backButtonStyle: React.CSSProperties = {
  backgroundColor: "#d4d0c8",
  border: "1px outset #ffffff",
  borderRadius: 0,
  height: 28,
  width: 30,
};

const EmailLinkTabPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Back returns to the page this one was opened from, however much
  // switching happens here (see useSmartBack).
  const handleBack = useSmartBack();

  // Which side of the mailbox is open — Customer Email or Dealer Email — now
  // a folder in the left sidebar instead of a separate route, so switching
  // never remounts this page.
  const [side, setSide] = useState<EmailSide>(
    searchParams.get("side") === "dealer" ? "dealer" : "customer",
  );
  const { linkType, keyLabel, keyPlaceholder, keyParam, multiSelectKey = false } =
    SIDE_CONFIG[side];

  // Deep-linkable: /customer-email?side=dealer&model=M1234 opens straight
  // onto a record, so other pages can link into the conversation for the
  // quote or model they are on.
  const initialKey = searchParams.get(keyParam) || "";
  const [activeKey, setActiveKey] = useState(initialKey);
  const [keyInput, setKeyInput] = useState(initialKey);

  // Each side remembers its own open record independently of the other, so
  // flipping Customer -> Dealer -> Customer comes back to the same quote
  // instead of landing back on an empty picker. Both are seeded from the
  // URL, not just the side being opened: a caller that knows the quote and
  // its models (the Sales View buttons) names both, so the side you didn't
  // land on is already loaded when you switch tabs over to it.
  const sideKeysRef = useRef<Record<EmailSide, string>>({
    customer: searchParams.get(SIDE_CONFIG.customer.keyParam) || "",
    dealer: searchParams.get(SIDE_CONFIG.dealer.keyParam) || "",
  });

  const [status, setStatus] = useState<MailboxSyncStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [linkedKeys, setLinkedKeys] = useState<LinkedKey[]>([]);
  const [syncing, setSyncing] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [connectForm] = Form.useForm<MailboxCredentials>();
  // Bumped after any mutation so every child refetches together.
  const [refreshToken, setRefreshToken] = useState(0);

  const bumpRefresh = useCallback(
    () => setRefreshToken((value) => value + 1),
    [],
  );

  // Which tab is open, and the live summary the history panel reports up so it
  // can be shown in the tab bar instead of inside the panel.
  // Inbox is the landing tab — it is what there is to act on when the page is
  // opened cold. The exception is a deep link (?quoteId=Q1234), which names a
  // record precisely because that conversation is what the caller wants to see.
  const [activeTab, setActiveTab] = useState(
    initialKey ? "history" : "unmatched",
  );

  // Picks up a keyParam that lands on the URL after this page has already
  // mounted — browser back/forward, or anything else pushing a new record
  // onto the URL under a page that is already up. The initial useState above
  // only ever sees the URL as it was at mount, so without this the record
  // never opens and Linked history stays empty.
  useEffect(() => {
    const urlKey = searchParams.get(keyParam) || "";
    if (urlKey && urlKey !== activeKey) {
      setActiveKey(urlKey);
      setKeyInput(urlKey);
      sideKeysRef.current[side] = urlKey;
      setActiveTab("history");
    }
    // Only reacts to the URL changing underneath it; openRecord already keeps
    // activeKey and the URL in sync on its own.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, keyParam]);

  // Fallback for arriving on the dealer side with no ?model= on the URL —
  // the nav menu, which has no quote to build one from. Picks up the models
  // the Quotes page last saved for the quote you were looking at. (The
  // Dealer Email button on Sales View names them on the URL instead, so it
  // never reaches here.)
  useEffect(() => {
    if (side !== "dealer") return;
    if (searchParams.get("model")) return;
    try {
      const stored = JSON.parse(
        localStorage.getItem(DEALER_EMAIL_MODELS_STORAGE_KEY) || "[]",
      );
      if (Array.isArray(stored) && stored.length) {
        const joined = stored.join(",");
        const params = new URLSearchParams(searchParams);
        params.set("side", "dealer");
        params.set("model", joined);
        setSearchParams(params, { replace: true });
        // Set activeKey/keyInput directly (rather than leaving it to the
        // urlKey-sync effect above) so that effect sees the URL and the
        // state already agreeing and doesn't also yank the view over to
        // Linked history — this pre-fill only gets the picker ready, it
        // shouldn't decide which view you are looking at.
        setActiveKey(joined);
        setKeyInput(joined);
        sideKeysRef.current.dealer = joined;
      }
    } catch {
      // Corrupt or blocked localStorage — just skip the pre-fill.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [side]);

  // Switching Customer/Dealer swaps the record context — the other side's
  // key param (quoteId vs model) means nothing here, so the filters and
  // selection reset. The open record itself is remembered per side
  // (sideKeysRef) so the picker still shows it. Whichever view you were on
  // (Inbox, Linked history, Needs confirm) stays put — the side switch
  // shouldn't also decide which view you land on.
  const switchSide = (next: EmailSide) => {
    if (next === side) return;
    sideKeysRef.current[side] = activeKey;
    const restoredKey = sideKeysRef.current[next] || "";

    setSide(next);
    // Both sides' records stay on the URL, so a reload here comes back to
    // the same two records rather than only the one you happened to be on.
    const params = new URLSearchParams();
    params.set("side", next);
    (Object.keys(SIDE_CONFIG) as EmailSide[]).forEach((target) => {
      const value = sideKeysRef.current[target];
      if (value) params.set(SIDE_CONFIG[target].keyParam, value);
    });
    setSearchParams(params, { replace: true });

    setActiveKey(restoredKey);
    setKeyInput(restoredKey);
    setUnmatchedInput("");
    setUnmatchedSearch("");
    setHistorySearch("");
    setHistoryActions(null);
    setUnmatchedActions(null);
  };

  // Each list reports the selected message's actions up; the tab bar renders
  // them so no tab needs a toolbar row of its own.
  const [historyActions, setHistoryActions] = useState<React.ReactNode>(null);

  // The Unmatched tab's search box and Refresh live in the tab bar, so the
  // filter is owned here and handed down to the list.
  const [unmatchedInput, setUnmatchedInput] = useState("");
  const [unmatchedSearch, setUnmatchedSearch] = useState("");
  // Same idea for Linked history — filters the already-loaded thread by
  // sender/subject, client-side, so no extra request is needed.
  const [historySearch, setHistorySearch] = useState("");
  // Which folder the sidebar has open — Inbox (all unmatched) or Sent Mail
  // (unmatched replies we sent), both served by the same Unmatched tab.
  const [mailFolder, setMailFolder] = useState<"inbox" | "sent">("inbox");
  // The selected unmatched email's actions, reported up by the list.
  const [unmatchedActions, setUnmatchedActions] =
    useState<React.ReactNode>(null);

  const loadStatus = useCallback(async () => {
    try {
      setStatus(await getMailboxStatus());
    } catch (error: any) {
      // A failed status check must not blank the page — the history below reads
      // from the portal's own tables and works regardless.
      console.error("Could not load email sync status:", error?.message);
    } finally {
      setStatusLoading(false);
    }
  }, []);

  const loadLinkedKeys = useCallback(async () => {
    try {
      setLinkedKeys(await getLinkedKeys(linkType));
    } catch (error: any) {
      console.error("Could not load linked keys:", error?.message);
    }
  }, [linkType]);

  // The Inbox tab's badge, fetched for both sides independently of what's
  // actually on screen — the count used to come from the mounted Unmatched
  // list, which meant it went blank the moment you left Inbox for the other
  // side. This stays put no matter which tab is open.
  const [sideInboxCounts, setSideInboxCounts] = useState<
    Record<EmailSide, number | null>
  >({ customer: null, dealer: null });

  const loadSideInboxCounts = useCallback(async () => {
    try {
      const [customerInbox, dealerInbox] = await Promise.all([
        getUnmatchedEmails({ linkType: "quote", limit: 1 }),
        getUnmatchedEmails({ linkType: "dealer", limit: 1 }),
      ]);
      setSideInboxCounts({
        customer: customerInbox.total,
        dealer: dealerInbox.total,
      });
    } catch (error: any) {
      console.error("Could not load side inbox counts:", error?.message);
    }
  }, []);

  useEffect(() => {
    loadStatus();
    loadLinkedKeys();
    loadSideInboxCounts();
  }, [loadStatus, loadLinkedKeys, loadSideInboxCounts, refreshToken]);

  // Mail shows up in the Inbox as it is synced. The sync itself (the button, or
  // the backend's 5-minute run) saves each message as it goes, so while the page
  // is open the status is checked - every few seconds during a sync, every 30s
  // otherwise - and when the stored message count moves, the Inbox list and its
  // badge quietly pick up the new mail.
  const [liveToken, setLiveToken] = useState(0);
  const lastIndexedRef = useRef<number | null>(null);
  useEffect(() => {
    if (status && lastIndexedRef.current === null) {
      lastIndexedRef.current = status.indexedMessages;
    }
  }, [status]);
  const mailboxConnected = Boolean(status?.connected);
  const syncRunning = syncing || Boolean(status?.syncing);
  useEffect(() => {
    if (!mailboxConnected) return undefined;
    const timer = window.setInterval(
      async () => {
        try {
          const next = await getMailboxStatus();
          setStatus(next);
          if (
            lastIndexedRef.current !== null &&
            next.indexedMessages !== lastIndexedRef.current
          ) {
            setLiveToken((value) => value + 1);
            loadSideInboxCounts();
          }
          lastIndexedRef.current = next.indexedMessages;
        } catch {
          // Status is only a hint - try again on the next tick.
        }
      },
      syncRunning ? 4000 : 30000,
    );
    return () => window.clearInterval(timer);
  }, [mailboxConnected, syncRunning, loadSideInboxCounts]);

  // While a sync is paused on Gmail's limit (or the backend is resuming one on
  // its own), keep the status fresh so the warning clears by itself and the
  // newly fetched mail shows up without the user having to reload.
  const syncWaiting = Boolean(status?.syncPause || status?.syncing);
  const wasWaitingRef = useRef(false);
  useEffect(() => {
    if (wasWaitingRef.current && !syncWaiting && !syncing) {
      message.success("Email sync finished after the Gmail limit reset");
      bumpRefresh();
    }
    wasWaitingRef.current = syncWaiting;
    if (!syncWaiting || syncing) return undefined;
    const timer = window.setInterval(loadStatus, 15000);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncWaiting, syncing, loadStatus]);

  // The badge on the Customer Email / Dealer Email tabs: how many messages
  // the record open on that side actually has. Both sides are asked the same
  // question through the same endpoint the Linked history panel uses, so the
  // two badges agree with each other and with the panel, and neither changes
  // just because you looked at the other one. (Summing each selected key's
  // count from the linked-keys list looks equivalent but is not — one
  // message linked to two selected models counts twice there and once here.)
  const [sideRecordCounts, setSideRecordCounts] = useState<
    Record<EmailSide, number | null>
  >({ customer: null, dealer: null });

  // Stable, and a no-op when the number hasn't moved — the panel reports
  // through this on every load, so an unconditional setState here would
  // re-render the panel's callback prop and feed itself.
  const reportSideCount = useCallback(
    (target: EmailSide, total: number | null) => {
      setSideRecordCounts((current) =>
        current[target] === total ? current : { ...current, [target]: total },
      );
    },
    [],
  );

  const loadSideRecordCounts = useCallback(async () => {
    const countFor = async (target: EmailSide) => {
      const openKey =
        target === side ? activeKey : sideKeysRef.current[target] || "";
      if (!openKey) return null;
      const recordKey: RecordKey =
        target === "customer"
          ? { linkType: "quote", quoteId: openKey }
          : { linkType: "dealer", model: openKey };
      const response = await getEmailThread(recordKey);
      return response.messages.length;
    };

    try {
      const [customer, dealer] = await Promise.all([
        countFor("customer"),
        countFor("dealer"),
      ]);
      reportSideCount("customer", customer);
      reportSideCount("dealer", dealer);
    } catch (error: any) {
      console.error("Could not load record counts:", error?.message);
    }
  }, [side, activeKey, reportSideCount]);

  useEffect(() => {
    loadSideRecordCounts();
  }, [loadSideRecordCounts, refreshToken]);

  // The mounted panel keeps its own side's badge honest between those loads
  // — after an unlink, say, without waiting for a full refresh.
  const handleHistoryCount = useCallback(
    (total: number) => reportSideCount(side, total),
    [reportSideCount, side],
  );

  const openRecord = (value: string) => {
    const next = value.trim();
    setActiveKey(next);
    setKeyInput(next);
    sideKeysRef.current[side] = next;
    const params = new URLSearchParams(searchParams);
    if (next) params.set(keyParam, next);
    else params.delete(keyParam);
    setSearchParams(params, { replace: true });
  };

  // One click connects the account emails are already sent from (the sending
  // account on the Settings page) - the backend verifies it against the IMAP
  // and SMTP servers before saving. If that fails, or the user wants another
  // mailbox, the form below takes a typed-in email and password instead.
  const connectWith = async (values: Partial<MailboxCredentials>, fromForm: boolean) => {
    setConnecting(true);
    try {
      const result = await connectMailbox({
        email: "",
        password: "",
        ...values,
        imapPort: values.imapPort ? Number(values.imapPort) : undefined,
        smtpPort: values.smtpPort ? Number(values.smtpPort) : undefined,
      });
      setConnectOpen(false);
      connectForm.resetFields();
      // Await the refetch rather than only bumping the token: the button stays
      // in its loading state until the page is actually showing the mailbox.
      await Promise.all([loadStatus(), loadLinkedKeys()]);
      bumpRefresh();
      message.success(result.message || "Email connected");
    } catch (error: any) {
      message.error(
        error?.response?.data?.message || error?.message || "Could not connect the mailbox",
        6,
      );
      // The automatic attempt failed - let the user type the login in.
      if (!fromForm) setConnectOpen(true);
    } finally {
      setConnecting(false);
    }
  };

  const handleConnect = () => connectWith({}, false);

  const handleConnectForm = async () => {
    let values: MailboxCredentials;
    try {
      values = await connectForm.validateFields();
    } catch {
      return; // field errors are already shown on the form
    }
    await connectWith(values, true);
  };

  const confirmDisconnect = () => {
    Modal.confirm({
      title: "Disconnect email?",
      content:
        "The linked history is hidden and syncing stops. Nothing is deleted — it all comes back when you reconnect.",
      okText: "Disconnect",
      okButtonProps: { danger: true },
      onOk: handleDisconnect,
    });
  };

  const confirmBackfill = () => {
    Modal.confirm({
      title: "Run the historical backfill?",
      content:
        "Pulls the full history window. It is safe to re-run - mail already saved is skipped - but it can take several minutes.",
      okText: "Run backfill",
      onOk: () => handleSync(true),
    });
  };

  const handleDisconnect = async () => {
    try {
      await disconnectMailbox();
      message.success("Email disconnected");
      // Clear the screen with it: the open record, the filter, the selected
      // message and its actions all belong to the mailbox that just went away.
      openRecord("");
      setKeyInput("");
      setLinkedKeys([]);
      setUnmatchedInput("");
      setUnmatchedSearch("");
      setHistoryActions(null);
      setUnmatchedActions(null);
      setActiveTab("unmatched");
      bumpRefresh();
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Could not disconnect");
    }
  };

  const handleSync = async (initial: boolean) => {
    setSyncing(true);
    // This click reports its own outcome; don't also toast "finished after
    // the limit reset" when the status refetch below clears the pause.
    wasWaitingRef.current = false;
    // Drop the previous run's error as this one starts. The backend clears it
    // on success and overwrites it on failure, so the refetch below settles it
    // either way — this just stops a stale message hanging around meanwhile.
    setStatus((current) => (current ? { ...current, lastError: null } : current));
    try {
      const result: any = await triggerEmailSync(
        initial ? { initial: true } : {},
      );
      if (result?.paused) {
        // Gmail's per-minute limit — a pause, not a failure. What was fetched
        // is saved and the backend carries on from that point by itself.
        message.warning(
          result.skipped
            ? `Gmail limit reached — sync continues automatically at ${formatDateTime(result.resumeAt)}`
            : `Gmail limit reached — ${result?.messages ?? 0} new emails saved so far. The rest will continue automatically at ${formatDateTime(result.resumeAt)}`,
          6,
        );
      } else if (result?.skipped) {
        message.info(result.reason || "A sync is already running");
      } else {
        message.success(
          `Sync complete — ${result?.messages ?? 0} new, ${result?.updated ?? 0} updated, ${result?.links ?? 0} links`,
        );
      }
      bumpRefresh();
    } catch (error: any) {
      if (error?.response?.status === 429) {
        message.warning(error.response.data?.message || "Gmail limit reached");
        return;
      }
      message.error(
        error?.response?.data?.message || error?.message || "Sync failed",
      );
    } finally {
      setSyncing(false);
    }
  };

  // Nothing on this page means anything without a live mailbox behind it, so
  // one flag gates the picker, the tabs and everything they hold.
  const connected = Boolean(status?.configured && status?.connected);

  const tabLabelWithCount = (label: string, count: number | null) => (
    <span className="inline-flex items-center gap-1">
      {label}
      {count != null ? (
        <span className="text-xs text-gray-400">{count}</span>
      ) : null}
    </span>
  );

  const pickerOptions = linkedKeys.map((item) => ({
    value: item.key,
    label: (
      <div className="flex items-center justify-between gap-3">
        <span>{item.key}</span>
        <span className="text-xs text-gray-400">{item.count} emails</span>
      </div>
    ),
  }));

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-gray-50 px-4 pb-3 pt-2">
      {/* One toolbar row: who you are and what you are looking at on the left,
          mailbox state on the right. Everything the mailbox can do is folded
          into a single Sync menu so the row stays readable. */}
      <div className="mb-3 flex shrink-0 flex-nowrap items-start justify-between gap-x-4 gap-y-2 border-b border-gray-200 pb-3">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-2">
          <button
            type="button"
            className="common-header-nav-btn common-header-back-btn flex items-center justify-center text-black"
            style={backButtonStyle}
            onClick={handleBack}
            title="Back"
          >
            <LeftOutlined style={{ fontSize: 13 }} />
          </button>
          <NavigationDropdown />

          {/* One search slot on the toolbar, whose contents follow the tab:
              the record picker for the conversation tabs, the sender/subject
              search when you are working the Unmatched list. */}
          {connected ? (
            <div className="flex flex-nowrap items-center gap-2 whitespace-nowrap border-l border-gray-200 pl-3">
              {activeTab === "unmatched" ? null : (
                <>
                  <span className="self-center text-sm font-medium text-gray-600">
                    {keyLabel}
                  </span>
                  {multiSelectKey ? (
                    <Select
                      mode="multiple"
                      value={keyInput ? keyInput.split(",").filter(Boolean) : []}
                      options={pickerOptions}
                      onChange={(values: string[]) =>
                        setKeyInput(values.join(","))
                      }
                      filterOption={(input, option) =>
                        String(option?.value || "")
                          .toLowerCase()
                          .includes(input.trim().toLowerCase())
                      }
                      notFoundContent={null}
                      placeholder={keyPlaceholder}
                      allowClear
                      size="middle"
                      className={
                        "self-center " +
                        // Same fixed 32px height as the "Open" button beside
                        // it (App.css otherwise pins every .ant-select to
                        // 25px). A model list longer than one row scrolls
                        // inside that fixed height rather than growing the
                        // box — the scrollbar track is kept functional but
                        // hidden (both engines below) so it doesn't add a
                        // visible strip next to the chips.
                        "[&.ant-select]:!h-8 " +
                        "[&_.ant-select-selector]:!h-8 " +
                        "[&_.ant-select-selector]:!overflow-y-auto " +
                        "[&_.ant-select-selector]:[scrollbar-width:none] " +
                        "[&_.ant-select-selector::-webkit-scrollbar]:hidden " +
                        "[&_.ant-select-selection-placeholder]:!leading-[30px]"
                      }
                      style={{ minWidth: 220, maxWidth: 320 }}
                    />
                  ) : (
                    <AutoComplete
                      value={keyInput}
                      options={pickerOptions}
                      onChange={setKeyInput}
                      onSelect={openRecord}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") openRecord(keyInput);
                      }}
                      filterOption={(input, option) =>
                        String(option?.value || "")
                          .toLowerCase()
                          .includes(input.trim().toLowerCase())
                      }
                      notFoundContent={null}
                      placeholder={keyPlaceholder}
                      allowClear
                      size="middle"
                      className={
                        "self-center " +
                        // Undo the global App.css rules that pin every .ant-select to
                        // 25px and nudge its search input down 2px, so this box matches
                        // the 32px "Open" button beside it.
                        "[&.ant-select]:!h-8 [&_.ant-select-selector]:!h-8 " +
                        "[&_.ant-select-selection-search-input]:!mt-0 " +
                        "[&_.ant-select-selection-search-input]:!h-[30px] " +
                        "[&_.ant-select-selection-placeholder]:!leading-[30px] " +
                        "[&_.ant-select-selection-item]:!leading-[30px]"
                      }
                      style={{ width: 180, maxWidth: "100%" }}
                    />
                  )}
                  <Button
                    type="primary"
                    className={PRIMARY_BUTTON_CLASS}
                    onClick={() => openRecord(keyInput)}
                  >
                    Open
                  </Button>
                  {activeKey ? (
                    <Button onClick={() => openRecord("")}>Clear</Button>
                  ) : null}
                </>
              )}
            </div>
          ) : null}
        </div>

        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          {status?.configured && status?.connected ? (
            <>
              {/* The counts and last-sync time are detail, not headline — they
                  live in the tooltip on the connected dot. */}
              <Tooltip
                title={
                  <div className="text-xs">
                    <div>{status.indexedMessages} emails indexed</div>
                    <div>Last sync {formatDateTime(status.lastSyncAt)}</div>
                  </div>
                }
              >
                <Badge
                  status="success"
                  text={
                    <span className="text-sm text-gray-600">
                      {status.connectedUser || "mailbox"}
                    </span>
                  }
                />
              </Tooltip>
              <Dropdown
                trigger={["click"]}
                menu={{
                  items: [
                    {
                      key: "sync",
                      icon: <ReloadOutlined />,
                      label: "Sync now",
                    },
                    {
                      key: "backfill",
                      icon: <CloudSyncOutlined />,
                      label: "Historical sync",
                    },
                    { type: "divider" },
                    {
                      key: "disconnect",
                      danger: true,
                      label: "Disconnect email",
                    },
                  ],
                  onClick: ({ key }) => {
                    if (key === "sync") handleSync(false);
                    if (key === "backfill") confirmBackfill();
                    if (key === "disconnect") confirmDisconnect();
                  },
                }}
              >
                <Button icon={<ReloadOutlined />} loading={syncing}>
                  Sync <DownOutlined className="text-[10px]" />
                </Button>
              </Dropdown>
            </>
          ) : null}
          <PageLogoutButton />
        </div>
      </div>

      <div className="flex min-h-0 w-full flex-1 flex-col">
        {statusLoading ? (
          <div className="flex flex-1 items-center justify-center">
            <Spin size="large" />
          </div>
        ) : (
        <>
        <Modal
          title="Connect a different mailbox"
          open={connectOpen}
          onOk={handleConnectForm}
          onCancel={() => setConnectOpen(false)}
          okText="Connect"
          confirmLoading={connecting}
          destroyOnClose
        >
          <Form form={connectForm} layout="vertical" requiredMark={false}>
            <Form.Item
              name="email"
              label="Email address"
              rules={[
                { required: true, message: "Enter the email address" },
                { type: "email", message: "Enter a valid email address" },
              ]}
            >
              <Input autoComplete="off" placeholder="you@company.com" />
            </Form.Item>
            <Form.Item
              name="password"
              label="Password / app password"
              extra="Gmail and Google Workspace need an App Password (Google Account > Security > 2-Step Verification > App passwords)."
              rules={[{ required: true, message: "Enter the password" }]}
            >
              <Input.Password autoComplete="new-password" />
            </Form.Item>
            <Button type="link" className="p-0 mb-2" onClick={() => setShowAdvanced((v) => !v)}>
              {showAdvanced ? "Hide server settings" : "Server settings (optional)"}
            </Button>
            {showAdvanced ? (
              <div className="grid grid-cols-2 gap-x-3">
                <Form.Item name="imapHost" label="IMAP host">
                  <Input placeholder="imap.example.com" />
                </Form.Item>
                <Form.Item name="imapPort" label="IMAP port">
                  <Input placeholder="993" />
                </Form.Item>
                <Form.Item name="smtpHost" label="SMTP host">
                  <Input placeholder="smtp.example.com" />
                </Form.Item>
                <Form.Item name="smtpPort" label="SMTP port">
                  <Input placeholder="465" />
                </Form.Item>
              </div>
            ) : null}
            <div className="text-xs text-gray-500">
              Mail is read over IMAP and replies are sent over SMTP. Server names are
              filled in automatically for Gmail, Outlook and Yahoo.
            </div>
          </Form>
        </Modal>

        {/* The connected + syncing controls now live in the top bar. This card
            only shows when something needs the user's attention: email not
            configured, not connected yet, or the last sync reported an error. */}
        {!status?.configured ? (
          <Card size="small" className="mb-4">
            <Alert
              type="warning"
              showIcon
              message="Email is not configured yet"
              description="The email service is not available. Reload this page, or contact an administrator if it persists."
            />
          </Card>
        ) : !status?.connected ? (
          <Card size="small" className="mb-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <Badge
                status="error"
                text={<span className="text-sm">Email not connected</span>}
              />
              <div className="flex items-center gap-2">
                <Button
                  type="primary"
                  className={PRIMARY_BUTTON_CLASS}
                  icon={<LinkOutlined />}
                  loading={connecting}
                  onClick={handleConnect}
                >
                  Connect Email
                </Button>
                <Button type="link" onClick={() => setConnectOpen(true)}>
                  Use a different mailbox
                </Button>
              </div>
            </div>
          </Card>
        ) : null}

        {status?.syncPause && connected ? (
          <Card size="small" className="mb-4">
            <Alert
              type="warning"
              showIcon
              message="Gmail usage limit reached — sync paused"
              description={
                <div className="text-sm">
                  <div>
                    All emails fetched so far are saved. The sync will continue{" "}
                    <b>automatically from where it stopped</b> at{" "}
                    {formatDateTime(status.syncPause.resumeAt)} — nothing is lost and
                    nothing is downloaded twice.
                  </div>
                  {status.syncPause.mode === "initial" || status.backfillPending ? (
                    <div className="mt-1">
                      To fetch the remaining old history yourself after that time,
                      click <b>Sync → Historical sync</b>. It continues from the
                      paused point, not from the beginning.
                    </div>
                  ) : null}
                </div>
              }
              action={
                <Button
                  size="small"
                  loading={syncing}
                  disabled={
                    new Date(status.syncPause.resumeAt).getTime() > Date.now()
                  }
                  onClick={() =>
                    handleSync(
                      status.syncPause?.mode === "initial" ||
                        Boolean(status.backfillPending),
                    )
                  }
                >
                  Continue now
                </Button>
              }
            />
          </Card>
        ) : status?.backfillPending && connected && !syncing && !status?.syncing ? (
          <Card size="small" className="mb-4">
            <Alert
              type="info"
              showIcon
              message="Historical sync not finished"
              description="Part of the old email history is still to be fetched. Click Sync → Historical sync to continue from where it stopped."
            />
          </Card>
        ) : null}

        {status?.lastError && !syncing ? (
          <Card size="small" className="mb-4">
            <Alert
              type="error"
              showIcon
              message="Last sync reported an error"
              description={status.lastError}
            />
          </Card>
        ) : null}

        {connected ? (
          <div className="flex min-h-0 flex-1 gap-3">
            {/* Outlook's own folder pane, to the left of the content — Sent
                Mail, Linked history and Needs confirm live here; Inbox sits
                up top next to the Customer/Dealer tabs instead. */}
            <EmailFolderSidebar
              connectedUser={status?.connectedUser || ""}
              sentActive={activeTab === "unmatched" && mailFolder === "sent"}
              onSelectSent={() => {
                setMailFolder("sent");
                setActiveTab("unmatched");
              }}
              historyActive={activeTab === "history"}
              onSelectHistory={() => setActiveTab("history")}
              pendingActive={activeTab === "pending"}
              onSelectPending={() => setActiveTab("pending")}
            />

            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              {/* Which mailbox side is open — Customer Email or Dealer
                  Email — plus Inbox right beside them, sitting above the
                  content column only so the sidebar stays full height. */}
              <Tabs
                activeKey={
                  activeTab === "unmatched" && mailFolder === "inbox"
                    ? "inbox"
                    : side
                }
                onChange={(key) => {
                  if (key === "inbox") {
                    // Inbox always opens on the Customer side with a clean
                    // /customer-email URL. Without this, coming back from the
                    // Dealer tab kept ?side=dealer on the URL and showed the
                    // dealer's Inbox count instead. Each side's open record is
                    // still remembered in sideKeysRef for when its tab is
                    // reopened.
                    if (side !== "customer") {
                      sideKeysRef.current[side] = activeKey;
                      const restoredKey = sideKeysRef.current.customer || "";
                      setSide("customer");
                      setActiveKey(restoredKey);
                      setKeyInput(restoredKey);
                      setHistorySearch("");
                      setHistoryActions(null);
                      setUnmatchedActions(null);
                    }
                    setUnmatchedInput("");
                    setUnmatchedSearch("");
                    setSearchParams(new URLSearchParams(), { replace: true });
                    setMailFolder("inbox");
                    setActiveTab("unmatched");
                    return;
                  }
                  const next = key as EmailSide;
                  if (next !== side) switchSide(next);
                  // Inbox has its own tab now, so Customer Email / Dealer
                  // Email always land on a non-Inbox view — otherwise
                  // clicking one while already on that side and sitting in
                  // Inbox would look like nothing happened.
                  if (activeTab === "unmatched") setActiveTab("history");
                }}
                className="shrink-0 [&_.ant-tabs-nav]:!mb-3"
                tabBarStyle={{ justifyContent: "flex-end" }}
                items={[
                  {
                    key: "inbox",
                    // Inbox always means the Customer inbox, so its badge must not
                    // change when the Dealer tab is open.
                    label: tabLabelWithCount("Inbox", sideInboxCounts.customer),
                  },
                  {
                    key: "customer",
                    label: tabLabelWithCount(
                      "Customer Email",
                      sideRecordCounts.customer,
                    ),
                  },
                  {
                    key: "dealer",
                    label: tabLabelWithCount(
                      "Dealer Email",
                      sideRecordCounts.dealer,
                    ),
                  },
                ]}
              />

              {/* The current folder's name plus whatever actions the list
                  below reports up (reply, attach, etc.) — this used to live
                  in the tab bar's extra-content slot. */}
              <div className="mb-3 flex shrink-0 items-start justify-between gap-2">
                {activeTab === "unmatched" && mailFolder === "inbox" ? (
                  <Input
                    value={unmatchedInput}
                    onChange={(event) => {
                      const next = event.target.value;
                      setUnmatchedInput(next);
                      if (!next.trim() && unmatchedSearch)
                        setUnmatchedSearch("");
                    }}
                    onPressEnter={() =>
                      setUnmatchedSearch(unmatchedInput.trim())
                    }
                    placeholder="Search sender or subject"
                    prefix={<SearchOutlined className="text-gray-400" />}
                    className="!w-72"
                    allowClear
                  />
                ) : activeTab === "history" ? (
                  <Input
                    value={historySearch}
                    onChange={(event) => setHistorySearch(event.target.value)}
                    placeholder="Search sender or subject"
                    prefix={<SearchOutlined className="text-gray-400" />}
                    className="!w-72"
                    allowClear
                  />
                ) : (
                  <h2 className="m-0 text-base font-semibold text-gray-800">
                    {activeTab === "unmatched" ? "Sent Mail" : "Needs confirm"}
                  </h2>
                )}
                {activeTab === "unmatched"
                  ? unmatchedActions
                  : activeTab === "history" && activeKey
                    ? historyActions
                    : null}
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto pr-1">
                {activeTab === "unmatched" ? (
                  <UnmatchedEmailList
                    linkType={linkType}
                    keyLabel={keyLabel}
                    onAttached={bumpRefresh}
                    refreshToken={refreshToken}
                    liveToken={liveToken}
                    search={unmatchedSearch}
                    direction={mailFolder === "sent" ? "out" : undefined}
                    onActions={setUnmatchedActions}
                  />
                ) : activeTab === "history" ? (
                  <EmailThreadPanel
                    linkType={linkType}
                    recordKey={activeKey}
                    keyLabel={keyLabel}
                    refreshToken={refreshToken}
                    search={historySearch}
                    onActions={setHistoryActions}
                    onCount={handleHistoryCount}
                  />
                ) : (
                  <PendingLinkList
                    linkType={linkType}
                    keyLabel={keyLabel}
                    onResolved={bumpRefresh}
                    refreshToken={refreshToken}
                  />
                )}
              </div>
            </div>
          </div>
        ) : null}
        </>
        )}
      </div>
    </div>
  );
};

export default EmailLinkTabPage;
